import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";

import { TemplateLayout } from "../templates/TemplateLayout";
import {
  EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG,
  ExperienceVerificationTemplateFormType,
  validationSchema,
} from "./utils";
import { useClientPortalAPI, useSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { ExperienceVerificationTemplateForm } from "./ExperienceVerificationTemplateForm";
import { Loader, SectionMessage } from "@certn/cascadia-react";
import { Schemas } from "../../types";

export function ExperienceVerificationTemplateUpdatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { enableSettingEducationLevelInExperienceVerificationTemplates } =
    useFlags();

  const goBack = () => navigate("/settings/experience-verification-templates");

  const { data, mutate, isLoading, error } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/experience-verification/${templateId}/`
  );

  const { submit } = useSubmit(
    `/api/internal/client-portal/check-arguments-templates/experience-verification/${templateId}/update/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: async () => {
        await mutate();
        showToast(t("Template updated successfully!"), { type: "success" });
        goBack();
      },
    }
  );

  const check_arguments = data?.check_arguments as {
    lookback_period_months: number;
    include_gap_detection?: boolean;
    gap_threshold_unit?: "MONTHS" | "DAYS";
    minimum_gap_period_months?: number;
    minimum_gap_period_days?: number;
    gap_verification_type?: Schemas["GapVerificationTypeEnum"];
    reference_rule_options?: Schemas["ReferenceRuleOptionsEnum"];
    include_cumulative_gap_detection?: boolean;
    verification_type?: Schemas["ExperienceEducationLevelEnum"] | null;
    digital_verification?: boolean;
  };

  if (isLoading) {
    return <Loader overlay />;
  }

  if (error) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t("Unable to retrieve template.")}
        icon="error"
      >
        {error.message}
      </SectionMessage>
    );
  }

  if (!data || !check_arguments) {
    return (
      <SectionMessage
        icon="warning"
        sectionTitle={t("Unable to retrieve template information")}
        type="danger"
      />
    );
  }

  const includeGapDetection = check_arguments.include_gap_detection !== false;
  const gapUnit = check_arguments.gap_threshold_unit ?? "MONTHS";
  const minimumGapMonths =
    gapUnit === "MONTHS"
      ? check_arguments.minimum_gap_period_months ??
        EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_MINIMUM_GAP_PERIOD_MONTHS
      : undefined;
  const minimumGapDays =
    gapUnit === "DAYS" ? check_arguments.minimum_gap_period_days : undefined;

  const updateTemplate = async (name?: string) => {
    await submit({
      name,
    });
  };

  return (
    <Formik<ExperienceVerificationTemplateFormType>
      enableReinitialize
      validateOnMount
      initialValues={{
        name: data.name,
        lookback_period_years: check_arguments.lookback_period_months / 12,
        include_gap_detection: includeGapDetection,
        gap_threshold_unit: gapUnit,
        minimum_gap_period_months: minimumGapMonths,
        minimum_gap_period_days: minimumGapDays,
        gap_verification_type:
          check_arguments.gap_verification_type ?? "STATEMENT",
        reference_rule_options: check_arguments.reference_rule_options,
        include_cumulative_gap_detection:
          check_arguments.include_cumulative_gap_detection ?? false,
        ...(enableSettingEducationLevelInExperienceVerificationTemplates
          ? {
              verification_type:
                check_arguments.verification_type ??
                "EDUCATION_VERIFICATIONS_WITHIN_LOOKBACK",
            }
          : {}),
        digital_verification: check_arguments.digital_verification,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        await updateTemplate(values.name);
      }}
    >
      {({ values, handleSubmit, isSubmitting, isValid, setValues, dirty }) => {
        return (
          <TemplateLayout
            hideCancelModal
            type="update"
            title={t("Experience Verification template details")}
            onComplete={handleSubmit}
            submitting={isSubmitting}
            onCancel={goBack}
            disableCompletion={!isValid || !dirty}
          >
            <ExperienceVerificationTemplateForm
              isDisabled
              values={values}
              setValues={setValues}
            />
          </TemplateLayout>
        );
      }}
    </Formik>
  );
}
